/**
 * <s-scroll-reveal> — one per page (in layout/theme.liquid). Scroll effects for the whole store.
 *
 * 1. Reveal: marks <html> with .reveal-ready so the hidden "before" state only applies when this
 *    script runs; without JS (or with reduced motion) everything is simply visible.
 *      [data-reveal]          — the element fades up (data-reveal="zoom" scales an image in instead)
 *      [data-reveal-stagger]  — direct children fade up one after another
 *    Each target gets data-revealed once it enters the viewport, then is no longer observed.
 * 2. Parallax: .hero-parallax elements drift down and zoom slightly as the page scrolls past them.
 */
class SScrollReveal extends HTMLElement {
  #observer;
  #ac;
  #ticking = false;

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.#ac = new AbortController();
    this.#setupReveal();
    this.#setupParallax();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#ac?.abort();
  }

  #setupReveal() {
    if (!('IntersectionObserver' in window)) return;
    const targets = document.querySelectorAll('[data-reveal], [data-reveal-stagger]');
    if (!targets.length) return;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.setAttribute('data-revealed', '');
        this.#observer.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    for (const target of targets) {
      // Anything already on screen at load shows straight away, so the first view never flashes.
      const rect = target.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.85 && rect.bottom > 0) {
        target.setAttribute('data-revealed', '');
      } else {
        this.#observer.observe(target);
      }
    }
    document.documentElement.classList.add('reveal-ready');
  }

  #setupParallax() {
    const layers = [...document.querySelectorAll('.hero-parallax')];
    if (!layers.length) return;

    const update = () => {
      this.#ticking = false;
      const viewport = window.innerHeight || 1;
      const y = Math.min(window.scrollY, viewport);
      for (const layer of layers) {
        layer.style.transform = `translate3d(0, ${(y * 0.25).toFixed(1)}px, 0) scale(${(1 + (y / viewport) * 0.08).toFixed(3)})`;
      }
    };
    window.addEventListener('scroll', () => {
      if (this.#ticking) return;
      this.#ticking = true;
      requestAnimationFrame(update);
    }, { passive: true, signal: this.#ac.signal });
    update();
  }
}

if (!customElements.get('s-scroll-reveal')) {
  customElements.define('s-scroll-reveal', SScrollReveal);
}
